import { ProductModel } from '@models/product';
import React from 'react';
import { ReactComponent as StarOn } from '@assets/images/app/star-pink.svg';
import { ReactComponent as StarOff } from '@assets/images/app/star-gray.svg';
import { REVIEW_FACE_VALUES } from '@models/user-contents';

const GRADE_FULL_NAME: { [key: string]: string } = {
  유아: '미취학아동',
  초1: '초등학교 1학년',
  초2: '초등학교 2학년',
  초3: '초등학교 3학년',
  초4: '초등학교 4학년',
  초5: '초등학교 5학년',
  '초6+': '초등학교 6학년 이상',
};
export const ProductStatistics: React.FC<{ data: ProductModel }> = (props) => {
  const data = props.data;
  const statistics = data.statistics;
  const age = statistics?.age || {};
  const totalReviewCount = statistics?.reviewCount || 0;
  const ageValues: { [key: string]: number } = { 유아: 0, 초1: 0, 초2: 0, 초3: 0, 초4: 0, 초5: 0, '초6+': 0 };
  Object.keys(age).forEach((key) => {
    if (['초6', '중1', '중2', '중3', '고1', '고2', '고3'].includes(key)) {
      ageValues['초6+'] = ageValues['초6+'] + age[key];
    } else {
      ageValues[key] = age[key];
    }
  });
  const maxAgeName = Object.keys(ageValues).reduce((max, key) => {
    return ageValues[key] > ageValues[max] ? key : max;
  }, '유아');
  const totalAgeCount = Object.values(ageValues).reduce((sum, count) => sum + count, 0);
  const maxAgeCount = ageValues[maxAgeName];
  const maxAgePercentage = maxAgeCount > 0 ? Math.round((maxAgeCount / totalAgeCount) * 100) : 0;
  let graphHeightCorrectionRatio = 1;
  if (maxAgePercentage < 70) {
    graphHeightCorrectionRatio = 70 / maxAgePercentage;
  }
  const reviewRate = Number((statistics?.reviewRating || 5).toFixed(1));

  // 전체 합계 계산
  // 그래프 데이터 생성
  const graphData = [
    { age: '유아', count: ageValues['유아'] },
    { age: '초1', count: ageValues['초1'] },
    { age: '초2', count: ageValues['초2'] },
    { age: '초3', count: ageValues['초3'] },
    { age: '초4', count: ageValues['초4'] },
    { age: '초5', count: ageValues['초5'] },
    { age: '초6+', count: ageValues['초6+'] },
  ].map((item) => {
    const percentage = totalAgeCount > 0 ? Math.round((item.count / totalAgeCount) * 100) : 0;
    const height = totalAgeCount > 0 ? Math.round((item.count / totalAgeCount) * 60) : 0;
    return {
      age: item.age,
      percentage: `${percentage}%`,
      height: height * graphHeightCorrectionRatio,
    };
  });
  const childSatisfaction = statistics?.reviewRate5;
  const ChildSatisfactionIcon = childSatisfaction
    ? REVIEW_FACE_VALUES[`${Math.round(childSatisfaction)}`].Icon
    : undefined;

  if (totalAgeCount === 0 && totalReviewCount === 0) {
    return <></>;
  }

  return (
    <div style={{ margin: '24px 20px 0 20px' }}>
      {/* Title 섹션 */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '4px',
          paddingBottom: '4px',
        }}>
        {totalAgeCount > 0 && (
          <div
            style={{
              color: '#424242',
              fontFamily: 'Pretendard',
              fontSize: '18px',
              fontWeight: 700,
              lineHeight: '1.33',
              textAlign: 'left',
            }}>
            <span style={{ color: '#FF3D8F' }}>{`${GRADE_FULL_NAME[maxAgeName]}`}</span>
            <span>{`이 많이 경험했어요`}</span>
          </div>
        )}
        <div
          style={{
            color: '#808387',
            fontFamily: 'Pretendard',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '1.43',
            textAlign: 'left',
          }}>
          별점은 부모님의 평가, 아이 만족도는 아이의 반응을 담았어요.
        </div>
      </div>

      {/* Top 섹션 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          padding: '24px 0',
        }}>
        {/* Rate 섹션 */}
        {totalReviewCount > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '6px',
              width: '134px',
            }}>
            <div
              style={{
                color: '#9B9DA0',
                fontFamily: 'Pretendard',
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '1.42',
                textAlign: 'center',
              }}>
              {`후기 총 ${statistics?.reviewCount || 0}건`}
            </div>
            <div
              style={{
                color: '#242424',
                fontFamily: 'Pretendard',
                fontSize: '40px',
                fontWeight: 700,
                lineHeight: '1.2',
                textAlign: 'center',
              }}>
              {reviewRate}
            </div>
            <div style={{ display: 'flex', gap: '2px' }}>
              {[1, 2, 3, 4, 5].map((star) =>
                Math.round(reviewRate) >= star ? (
                  <StarOn key={star} style={{ width: '16px', height: '16px' }} />
                ) : (
                  <StarOff key={star} style={{ width: '16px', height: '16px' }} />
                ),
              )}
            </div>
          </div>
        )}
        {/* Graph 섹션 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
          {graphData.map((item, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2px',
                width: '24px',
              }}>
              <div
                style={{
                  color: '#9B9DA0',
                  fontFamily: 'Pretendard',
                  fontSize: '11px',
                  fontWeight: 500,
                  lineHeight: '1.45',
                  textAlign: 'right',
                }}>
                {item.percentage}
              </div>
              <div
                style={{
                  width: '6px',
                  height: '60px',
                  borderRadius: '10px',
                  background: '#E8EAED',
                  position: 'relative',
                }}>
                {item.height > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      width: '6px',
                      height: `${item.height}px`,
                      background: '#FF3D8F',
                      borderRadius: '10px',
                    }}
                  />
                )}
              </div>
              <div
                style={{
                  color: '#66686B',
                  fontFamily: 'Pretendard',
                  fontSize: '11px',
                  fontWeight: 500,
                  lineHeight: '1.45',
                  textAlign: 'right',
                }}>
                {item.age}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Bottom 섹션 */}
      {childSatisfaction !== undefined && childSatisfaction > 0 && (
        <div
          style={{
            background: '#FFF5F9',
            borderRadius: '8px',
            padding: '12px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '6px',
            width: '100%',
            height: '48px',
            marginBottom: '20px',
          }}>
          {ChildSatisfactionIcon && <ChildSatisfactionIcon style={{ width: '20px', height: '20px' }} />}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span
              style={{
                color: '#FF9EC7',
                fontFamily: 'Pretendard',
                fontSize: '14px',
                fontWeight: 700,
                lineHeight: '1.43',
                textAlign: 'center',
              }}>
              아이 만족도
            </span>
            <span
              style={{
                color: '#FF3D8F',
                fontFamily: 'Pretendard',
                fontSize: '14px',
                fontWeight: 700,
                lineHeight: '1.43',
                textAlign: 'center',
              }}>
              "최고! 다음에 또 하고싶어요!"
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
